import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import axios from "axios";
import { API_BASE_URL } from "../environment";
import { API } from "../common/Constants";
import { AuthContext } from "../context/AuthContext";
import "../styles/verification.css";

const VerificationSuccess = () => {
  const [status, setStatus] = useState("verifying");
  const [errorMessage, setErrorMessage] = useState("");
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";
  const { login } = useContext(AuthContext);

  const handleResendVerification = async () => {
    if (!email) {
      setErrorMessage("Email address is required to resend verification");
      return;
    }

    try {
      setStatus("resending");
      await axios.post(`${API_BASE_URL}${API.RESEND_VERIFICATION}`, { email });
      setStatus("resent");
    } catch (error) {
      console.error("Error resending verification:", error);
      setStatus("resend-error");
      setErrorMessage("Failed to resend verification email. Please try again.");
    }
  };

  useEffect(() => {
    let countdownInterval;
    let loginTimer;

    const verifyToken = async () => {
      if (verificationAttempted) {
        return;
      }
      setVerificationAttempted(true);

      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");

      if (!token) {
        setStatus("error");
        setErrorMessage("No verification token found in URL");
        return;
      }

      try {
        // Call the backend verification API
        const response = await axios.post(`${API_BASE_URL}${API.VERIFY}`, {
          token,
        });
        setStatus("success");

        // If verification is successful, attempt automatic login
        if (response.data && response.data.email) {
          try {
            // Start countdown timer
            const countdownInterval = setInterval(() => {
              setRedirectCountdown((prevCount) => {
                if (prevCount <= 1) {
                  clearInterval(countdownInterval);
                  return 0;
                }
                return prevCount - 1;
              });
            }, 1000);

            // Auto-login after 3 seconds to allow user to see success message
            setTimeout(async () => {
              // Store the authentication token and user info using AuthContext
              if (response.data.token) {
                login({
                  token: response.data.token,
                  email: response.data.email,
                  userId: response.data.userId,
                  fullName: response.data.fullName || "",
                });

                // Redirect to dashboard or onboarding page
                navigate("/dashboard");
              } else {
                // If token isn't provided, redirect to login
                navigate("/login", {
                  state: {
                    verifiedEmail: response.data.email,
                    justVerified: true,
                  },
                });
              }
            }, 3000);
          } catch (loginError) {
            console.error("Auto-login error:", loginError);
            // Still show success but don't auto-login
          }
        }
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("error");

        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          setErrorMessage(error.response.data.message);
        } else {
          setErrorMessage(
            "Verification failed. The link may be expired or invalid."
          );
        }
      }
    };

    verifyToken();

    // Cleanup function to clear all timers when component unmounts
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
      if (loginTimer) clearTimeout(loginTimer);
    };
  }, [navigate, login, verificationAttempted]);

  const handleLoginClick = () => {
    navigate("/login");
  };

  return (
    <section className="verification-success-page section-padding">
      <div className="container">
        <div className="verification-content">
          <div className="verification-icon">
            {status === "verifying" && (
              <FaSpinner size={80} color="#007BFF" className="spinner" />
            )}
            {status === "success" && (
              <FaCheckCircle size={80} color="#4CAF50" />
            )}
            {(status === "error" || status === "resend-error") && (
              <FaTimesCircle size={80} color="#F44336" />
            )}
            {status === "resending" && (
              <FaSpinner size={80} color="#007BFF" className="spinner" />
            )}
            {status === "resent" && <FaCheckCircle size={80} color="#4CAF50" />}
          </div>

          <h2>
            {status === "verifying" && "Email Verification"}
            {status === "success" && "Verification Successful"}
            {status === "error" && "Verification Failed"}
            {status === "resending" && "Resending Verification"}
            {status === "resent" && "Verification Email Sent"}
            {status === "resend-error" && "Resend Failed"}
          </h2>

          <p className="verification-message">
            {status === "verifying" &&
              "Please wait while we verify your email address..."}
            {status === "success" &&
              "Your email has been successfully verified. You are being automatically logged in..."}
            {status === "error" &&
              (errorMessage ||
                "Verification failed. The link may be expired or invalid.")}
            {status === "resending" && "Sending a new verification email..."}
            {status === "resent" &&
              "A new verification email has been sent. Please check your inbox."}
            {status === "resend-error" &&
              (errorMessage || "Failed to resend verification email.")}
          </p>

          {status === "success" && (
            <p className="redirect-timer">
              Redirecting to dashboard in{" "}
              <span className="countdown-number">{redirectCountdown}</span>{" "}
              second{redirectCountdown !== 1 ? "s" : ""}...
            </p>
          )}

          {status === "success" && (
            <button
              className="btn btn-primary login-btn"
              onClick={handleLoginClick}
            >
              Log In
            </button>
          )}

          {status === "error" && (
            <div className="error-actions">
              <p>If you need a new verification link:</p>
              <button
                className="btn btn-secondary resend-btn"
                onClick={handleResendVerification}
                disabled={!email || status === "resending"}
              >
                Resend Verification Email
              </button>
              <p>Or go back to:</p>
              <Link to="/register" className="btn btn-outline">
                Registration
              </Link>
            </div>
          )}

          {status === "resent" && (
            <p className="success-note">
              Please check your email inbox and click on the verification link.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default VerificationSuccess;
